<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import moment from "moment"
import { useI18n } from "vue-i18n"
import { useTaskStore } from "@core/stores/task.store"
import {
    EXPORT_EXTRAPOLATION_PV_TYPES,
    EXPORT_PV_SOURCE_TO_TASK,
    EXPORT_PV_TYPES,
    PV_EXTRAPOLATE_SOURCE_EXPORT,
    type ExportPvTypeKey,
} from "@modules/pv/constants/pv-export-tasks"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Identifiant source PDL (Mongo). */
    sourceId: string
    /** Analyse d’extrapolation courante, si présente. */
    idExtrapolation?: string | null
    /**
     * Document extrapolation (GET métier) pour bornes dates export « avec extrapolation ».
     * Peut être partiel tant que `charts` contient les points.
     */
    extrapolationDoc?: Record<string, unknown> | null
}>()

const visible = defineModel<boolean>("visible", { required: true })

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            dialogTitle: "Export de données",
            selectExport:
                "Veuillez sélectionner le logiciel dans lequel vous souhaitez importer les données de consommation",
            softwareLabel: "Logiciel cible",
            periodStart: "Début de période (inclus)",
            periodEnd: "Fin de période (inclus)",
            periodError:
                "La date de début doit être antérieure à la date de fin.",
            extrapolationTitle: "Export avec ou sans extrapolation ?",
            extrapolationLabelOn: "Avec extrapolation",
            extrapolationLabelOff: "Sans extrapolation",
            extrapolationMessage:
                "Les dates de l'export sont conditionnées par la période de l'extrapolation quand activée. La période d'extrapolation est de {startDate} à {endDate}.",
            cancel: "Annuler",
            submit: "Exporter les données",
            exportToastTitle: "Export {name}",
            exports: {
                [EXPORT_PV_TYPES.AUTOCALSOL]: "AutoCalSol",
                [EXPORT_PV_TYPES.PVSOL]: "PV*SOL",
                [EXPORT_PV_TYPES.ARCHELIOSPRO]: "Archelios PRO",
                [EXPORT_PV_TYPES.PYSYST]: "PVsyst",
                [EXPORT_PV_TYPES.SOLARIUS]: "Solarius",
                [EXPORT_PV_TYPES.FRONIUS_SOLAR]: "Fronius Solar.creator",
                [EXPORT_PV_TYPES.SOLAREDGE_DESIGNER]: "SolarEdge Designer",
                [EXPORT_PV_TYPES.SUNNY_DESIGN]: "Sunny Design",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const taskStore = useTaskStore()

// ================================
// STATE
// ================================
const loading = ref(false)
const exportTypeSelected = ref<string | null>(null)
const extrapolationToggle = ref(false)
const startDate = ref<Date | null>(null)
const endDate = ref<Date | null>(null)

// ================================
// COMPUTED
// ================================
const getExportOptions = computed(() =>
    (Object.keys(EXPORT_PV_TYPES) as ExportPvTypeKey[]).map((key) => ({
        label: t(`exports.${EXPORT_PV_TYPES[key]}`),
        value: EXPORT_PV_TYPES[key],
        extrapolateValue: EXPORT_EXTRAPOLATION_PV_TYPES[key],
    }))
)

const getIsValidDates = computed(() => {
    if (!startDate.value || !endDate.value) return true
    return moment(startDate.value).isSameOrBefore(moment(endDate.value), "day")
})

const getIsFormValid = computed(() => {
    return Boolean(
        exportTypeSelected.value &&
            startDate.value &&
            endDate.value &&
            getIsValidDates.value
    )
})

/** Période couverte par les points extrapolés (pour message + dates forcées). */
const getExtrapolationBounds = computed((): [Date, Date] | null => {
    const doc = props.extrapolationDoc
    if (!doc?.charts || typeof doc.charts !== "object") return null
    const charts = doc.charts as Record<string, unknown>
    const blockKeys = [
        "extrapolation_pv_hourly",
        "extrapolation_pv_daily",
    ] as const
    for (const bk of blockKeys) {
        const block = charts[bk] as Record<string, unknown> | undefined
        const subs = block?.subcharts as Record<string, unknown> | undefined
        const cons = subs?.consommation as Record<string, unknown> | undefined
        const content = cons?.content as Record<string, unknown> | undefined
        const pts = content?.points as { x?: unknown }[] | undefined
        if (!Array.isArray(pts) || pts.length === 0) continue
        const first = pts[0]?.x
        const last = pts[pts.length - 1]?.x
        const d0 = moment(first as string | Date)
        const d1 = moment(last as string | Date)
        if (d0.isValid() && d1.isValid()) return [d0.toDate(), d1.toDate()]
    }
    return null
})

const getExtrapolationMessage = computed(() => {
    if (!extrapolationToggle.value || !getExtrapolationBounds.value) return ""
    const [a, b] = getExtrapolationBounds.value
    return t("extrapolationMessage", {
        startDate: moment(a).format("DD/MM/YYYY"),
        endDate: moment(b).format("DD/MM/YYYY"),
    })
})

// ================================
// WATCHERS
// ================================
watch(extrapolationToggle, (on) => {
    const bounds = getExtrapolationBounds.value
    if (on && bounds) {
        startDate.value = bounds[0]
        endDate.value = bounds[1]
    }
})

watch(visible, (open) => {
    if (!open) {
        loading.value = false
        extrapolationToggle.value = false
        exportTypeSelected.value = null
        return
    }
    const end = moment().subtract(7, "day").endOf("day").toDate()
    const start = moment()
        .subtract(7, "day")
        .subtract(1, "year")
        .startOf("day")
        .toDate()
    startDate.value = start
    endDate.value = end
})

// ================================
// INTERACTIONS
// ================================
function onClose(): void {
    visible.value = false
}

/**
 * Soumet l’export : tâche Franz côté source (avec ou sans extrapolation).
 */
async function onSubmitExport(): Promise<void> {
    if (!getIsFormValid.value || !exportTypeSelected.value) return
    const sel = exportTypeSelected.value
    const opt = getExportOptions.value.find((o) => o.value === sel)
    if (!opt) return

    if (sel === EXPORT_PV_TYPES.PYSYST) {
        startDate.value = moment(startDate.value).startOf("year").toDate()
        endDate.value = moment(endDate.value).endOf("year").toDate()
    }

    const dateFrom = moment(startDate.value).format("YYYY-MM-DD")
    const dateTo = moment(endDate.value).format("YYYY-MM-DD")
    const taskName = EXPORT_PV_SOURCE_TO_TASK[sel]
    if (!taskName) return

    loading.value = true
    try {
        if (props.idExtrapolation && extrapolationToggle.value) {
            await taskStore.launchTask(
                PV_EXTRAPOLATE_SOURCE_EXPORT,
                {
                    _id_source: props.sourceId,
                    date_from: dateFrom,
                    date_to: dateTo,
                    logiciel: opt.extrapolateValue,
                    _id_extrapolation: props.idExtrapolation,
                },
                undefined,
                {
                    taskToastTitle: t("exportToastTitle", {
                        name: opt.label,
                    }),
                }
            )
        } else {
            await taskStore.launchTask(
                taskName,
                {
                    _id_source: props.sourceId,
                    date_from: dateFrom,
                    date_to: dateTo,
                },
                undefined,
                {
                    taskToastTitle: t("exportToastTitle", {
                        name: opt.label,
                    }),
                }
            )
        }
        visible.value = false
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :header="t('dialogTitle')"
        class="w-full max-w-2xl"
        :closable="!loading"
        :dismissable-mask="!loading"
        @hide="onClose"
    >
        <!-- ================================ -->
        <!-- Contenu -->
        <!-- ================================ -->
        <div class="flex flex-col gap-5">
            <p class="text-surface-600 text-sm">
                {{ t("selectExport") }}
            </p>

            <div v-if="idExtrapolation" class="flex flex-col gap-2">
                <p class="text-sm font-medium text-surface-800">
                    {{ t("extrapolationTitle") }}
                </p>
                <div class="flex items-center gap-3">
                    <BaseToggleSwitch
                        v-model="extrapolationToggle"
                        input-id="pv-export-extrapolation"
                    />
                    <label
                        class="text-sm text-surface-700 cursor-pointer"
                        for="pv-export-extrapolation"
                    >
                        {{
                            extrapolationToggle
                                ? t("extrapolationLabelOn")
                                : t("extrapolationLabelOff")
                        }}
                    </label>
                </div>
            </div>

            <BaseMessage
                v-if="extrapolationToggle && getExtrapolationMessage"
                severity="info"
                :closable="false"
            >
                {{ getExtrapolationMessage }}
            </BaseMessage>

            <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-surface-700">
                    {{ t("softwareLabel") }}
                </label>
                <BaseSelect
                    v-model="exportTypeSelected"
                    :options="getExportOptions"
                    option-label="label"
                    option-value="value"
                    :placeholder="t('selectExport')"
                    class="w-full"
                    :disabled="loading"
                />
            </div>

            <div
                v-if="exportTypeSelected"
                class="flex flex-wrap gap-4 items-start"
            >
                <div class="flex flex-col gap-1 min-w-[10rem] flex-1">
                    <label
                        class="text-sm font-medium text-surface-700"
                        for="pv-export-start"
                    >
                        {{ t("periodStart") }}
                    </label>
                    <BaseDatePicker
                        id="pv-export-start"
                        v-model="startDate"
                        date-format="dd/mm/yy"
                        :max-date="endDate ?? undefined"
                        :invalid="!getIsValidDates"
                        class="w-full"
                        :disabled="loading || extrapolationToggle"
                    />
                </div>
                <div class="flex flex-col gap-1 min-w-[10rem] flex-1">
                    <label
                        class="text-sm font-medium text-surface-700"
                        for="pv-export-end"
                    >
                        {{ t("periodEnd") }}
                    </label>
                    <BaseDatePicker
                        id="pv-export-end"
                        v-model="endDate"
                        date-format="dd/mm/yy"
                        :min-date="startDate ?? undefined"
                        :invalid="!getIsValidDates"
                        class="w-full"
                        :disabled="loading || extrapolationToggle"
                    />
                </div>
            </div>
            <p
                v-if="exportTypeSelected && !getIsValidDates"
                class="text-sm text-red-600"
            >
                {{ t("periodError") }}
            </p>

            <div class="flex justify-end gap-2 pt-2">
                <BaseButton
                    type="button"
                    :label="t('cancel')"
                    severity="secondary"
                    text
                    :disabled="loading"
                    @click="onClose"
                />
                <BaseButton
                    type="button"
                    :label="t('submit')"
                    :disabled="!getIsFormValid || loading"
                    :loading="loading"
                    @click="onSubmitExport"
                />
            </div>
        </div>
    </BaseDialog>
</template>
